/**
 * CRM Companies list — searchable, filterable table with create/edit/delete.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  SearchInput,
  Select,
  Badge,
  EmptyState,
  Button,
  SkeletonRow,
  ConfirmDialog,
  toast,
  Pagination,
} from '../../components/ui';
import { Building2, Globe, Plus, Edit3, Trash2 } from '../../lib/icons';
import { formatDate } from '../../lib/utils';
import { useT } from '../../lib/i18n';
import { usePersistedPageSize } from '../../hooks/use-persisted-page-size';
import { fetchCompanies, deleteCompany } from '../../lib/crm-api';
import type { CrmCompany } from '../../lib/crm-api';
import { CompanyFormDialog } from './forms';

export function CompaniesPage() {
  const t = useT();
  const [companies, setCompanies] = useState<CrmCompany[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [country, setCountry] = useState('');
  const [industry, setIndustry] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = usePersistedPageSize('crm.companies', 20);

  // Form state
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<CrmCompany | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CrmCompany | null>(null);

  // Debounce search
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    timerRef.current = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timerRef.current);
  }, [search]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchCompanies({
        search: debouncedSearch || undefined,
        country: country || undefined,
        industry: industry || undefined,
        limit: pageSize,
        offset: page * pageSize,
      });
      setCompanies(res.items);
      setTotal(res.total);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, country, industry, page, pageSize]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Reset page when filters or page size change
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, country, industry, pageSize]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteCompany(deleteTarget.id);
      toast(t('crm.companyDeleted'), 'success');
      setDeleteTarget(null);
      loadData();
    } catch {
      toast(t('crm.deleteFailed'), 'error');
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Filter bar */}
      <div className="border-b border-edge bg-surface-raised px-4 py-2.5 flex-shrink-0">
        <div className="flex items-center gap-2 flex-wrap">
          <SearchInput
            size="sm"
            className="flex-1 min-w-[120px] sm:flex-none sm:w-[200px]"
            value={search}
            onChange={setSearch}
            placeholder={t('crm.searchCompanies')}
          />
          <Select size="sm" inline value={country} onChange={(e) => setCountry(e.target.value)}>
            <option value="">{t('crm.allCountries')}</option>
            <option value="Germany">Germany</option>
            <option value="USA">USA</option>
            <option value="UK">UK</option>
            <option value="France">France</option>
            <option value="China">China</option>
            <option value="Japan">Japan</option>
          </Select>
          <Select size="sm" inline value={industry} onChange={(e) => setIndustry(e.target.value)}>
            <option value="">{t('crm.allIndustries')}</option>
            <option value="Retail">Retail</option>
            <option value="Wholesale">Wholesale</option>
            <option value="E-commerce">E-commerce</option>
            <option value="Manufacturing">Manufacturing</option>
            <option value="Agriculture">Agriculture</option>
          </Select>
          <div className="flex-1" />
          <span className="text-xs text-fg-faint">{total} results</span>
          <Button
            size="sm"
            onClick={() => {
              setEditTarget(null);
              setFormOpen(true);
            }}
          >
            <Plus size={14} />
            <span className="hidden sm:inline ml-1">Add Company</span>
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {!loading && companies.length === 0 ? (
          <EmptyState
            icon={Building2}
            title={t('crm.noCompaniesFound')}
            description={debouncedSearch || country || industry ? t('crm.adjustFilters') : t('crm.createFirstCompany')}
          />
        ) : (
          <table className="w-full min-w-[700px] [&_td]:whitespace-nowrap">
            <thead className="sticky top-0 z-10 bg-surface-muted [&_th]:whitespace-nowrap">
              <tr className="border-b border-edge bg-surface-muted">
                <th className="text-left text-xs font-medium text-fg-muted px-4 py-2">Name</th>
                <th className="text-left text-xs font-medium text-fg-muted px-4 py-2 hidden md:table-cell">Domain</th>
                <th className="text-left text-xs font-medium text-fg-muted px-4 py-2">Country</th>
                <th className="text-left text-xs font-medium text-fg-muted px-4 py-2 hidden lg:table-cell">Industry</th>
                <th className="text-left text-xs font-medium text-fg-muted px-4 py-2">Contacts</th>
                <th className="text-left text-xs font-medium text-fg-muted px-4 py-2 hidden md:table-cell">Source</th>
                <th className="text-left text-xs font-medium text-fg-muted px-4 py-2 hidden lg:table-cell">Created</th>
                <th className="w-16" />
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} cols={8} />)
                : companies.map((c) => (
                    <tr
                      key={c.id}
                      onClick={() => (window.location.hash = `#/crm/companies/${c.id}`)}
                      className="border-b border-edge hover:bg-surface-muted cursor-pointer transition-colors group"
                    >
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-md bg-surface-muted flex items-center justify-center flex-shrink-0">
                            <Building2 size={13} className="text-fg-muted" />
                          </div>
                          <span className="text-sm font-medium text-fg truncate" title={c.name}>
                            {c.name}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 hidden md:table-cell">
                        <span className="text-xs text-fg-secondary truncate" title={c.domain || undefined}>
                          {c.domain || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        {c.country ? (
                          <div className="flex items-center gap-1">
                            <Globe size={11} className="text-fg-faint" />
                            <span className="text-xs text-fg-secondary">{c.country}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-fg-faint">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 hidden lg:table-cell">
                        <span className="text-xs text-fg-secondary">{c.industry || '—'}</span>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="text-xs text-fg-secondary">{c.contact_count ?? 0}</span>
                      </td>
                      <td className="px-4 py-2.5 hidden md:table-cell">
                        {c.source ? (
                          <Badge>{c.source.replace(/_/g, ' ')}</Badge>
                        ) : (
                          <span className="text-xs text-fg-faint">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 hidden lg:table-cell">
                        <span className="text-[11px] text-fg-muted">{formatDate(c.created_at)}</span>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 touch-visible transition-opacity">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditTarget(c);
                              setFormOpen(true);
                            }}
                            className="p-1 rounded hover:bg-surface-muted text-fg-faint hover:text-fg-secondary"
                            title={t('common.edit')}
                          >
                            <Edit3 size={13} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTarget(c);
                            }}
                            className="p-1 rounded hover:bg-danger-subtle text-fg-faint hover:text-danger"
                            title={t('common.delete')}
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <Pagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} onPageSizeChange={setPageSize} />

      {/* Dialogs */}
      <CompanyFormDialog open={formOpen} onClose={() => setFormOpen(false)} onSaved={loadData} initial={editTarget} />
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title={t('crm.deleteCompany')}
        description={`Are you sure you want to delete "${deleteTarget?.name}"? This will also unlink all contacts.`}
        confirmLabel="Delete"
        confirmVariant="destructive"
      />
    </div>
  );
}
